import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import { useNotify, useRecordContext, useRefresh, useGetList } from 'react-admin';
import { Contract } from '../types';
import { getToken, getRefreshToken } from '../authentication/authProvider';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/';

interface RefundablePayment {
  payment_id: number;
  created_at: string;
  amount_cents: number;
  refunded_cents: number;
  refundable_cents: number;
  currency: string;
  payment_source: 'stripe' | 'partner' | 'other' | null;
  stripe_charge_id: string | null;
  is_within_stripe_window: boolean;
  near_window_warning: boolean;
  description: string;
}

const formatCents = (cents: number, currency = 'cad') =>
  new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: (currency || 'cad').toUpperCase(),
  }).format(cents / 100);

const RefundButton: React.FC = () => {
  const record = useRecordContext<Contract>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);

  if (!record) return null;

  return (
    <>
      <Button
        startIcon={<UndoIcon />}
        onClick={() => setOpen(true)}
        size="medium"
        sx={{ mr: 1 }}
      >
        Refund
      </Button>
      {open && (
        <RefundDialog
          contractId={Number(record.id)}
          onClose={() => setOpen(false)}
          onSuccess={(message) => {
            notify(message, { type: 'success', messageArgs: { _: message } });
            refresh();
            setOpen(false);
          }}
          onError={(message) =>
            notify(message, { type: 'error', messageArgs: { _: message } })
          }
        />
      )}
    </>
  );
};

interface DialogProps {
  contractId: number;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const RefundDialog: React.FC<DialogProps> = ({ contractId, onClose, onSuccess, onError }) => {
  const [payments, setPayments] = useState<RefundablePayment[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [amountDollars, setAmountDollars] = useState<string>('');
  const [reason, setReason] = useState<string>('requested_by_customer');
  const [notes, setNotes] = useState<string>('');
  const [qbPaymentMethodId, setQbPaymentMethodId] = useState<string>('');
  const [qbPaymentMethodLabel, setQbPaymentMethodLabel] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const response = await fetch(
          `${PAPERMAN_API_URL}contracts/${contractId}/refunds/refundable-payments`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Refresh-Token': await getRefreshToken(),
            },
          },
        );
        if (!response.ok) {
          throw new Error(`Failed to load payments (${response.status})`);
        }
        const data: RefundablePayment[] = await response.json();
        if (cancelled) return;
        setPayments(data);
        const firstRefundable = data.find((p) => p.refundable_cents > 0 && p.is_within_stripe_window);
        if (firstRefundable) setSelectedId(firstRefundable.payment_id);
      } catch (err: any) {
        if (cancelled) return;
        onError(err.message || 'Failed to load refundable payments');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contractId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedPayment = useMemo(
    () => payments?.find((p) => p.payment_id === selectedId) || null,
    [payments, selectedId],
  );
  const isStripe =
    selectedPayment?.payment_source === 'stripe' && !!selectedPayment.stripe_charge_id;
  const amountCents = Math.round(parseFloat(amountDollars || '0') * 100);
  const amountValid =
    selectedPayment != null &&
    Number.isFinite(amountCents) &&
    amountCents > 0 &&
    amountCents <= selectedPayment.refundable_cents;
  const manualValid = isStripe || (qbPaymentMethodId !== '' && qbPaymentMethodLabel !== '');
  const canSubmit = !submitting && selectedPayment != null && amountValid && manualValid;

  const handleSubmit = async () => {
    if (!selectedPayment) return;
    setSubmitting(true);
    try {
      const body: any = {
        payment_id: selectedPayment.payment_id,
        amount_cents: amountCents,
        reason,
        notes: notes || undefined,
      };
      if (!isStripe) {
        body.manual = {
          qb_payment_method_id: qbPaymentMethodId,
          method_label: qbPaymentMethodLabel,
        };
      }

      const token = await getToken();
      const response = await fetch(
        `${PAPERMAN_API_URL}contracts/${contractId}/refunds`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'Refresh-Token': await getRefreshToken(),
          },
          body: JSON.stringify(body),
        },
      );
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json?.message || `Refund failed (${response.status})`);
      }
      onSuccess(json?.message || 'Refund processed');
    } catch (err: any) {
      onError(err.message || 'Refund failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Refund payment</DialogTitle>
      <DialogContent>
        {loading && <Typography>Loading payments…</Typography>}
        {!loading && payments != null && payments.length === 0 && (
          <Alert severity="info">No payments are eligible for refund on this contract.</Alert>
        )}
        {!loading && payments != null && payments.length > 0 && (
          <Stack spacing={3} sx={{ pt: 1 }}>
            <FormControl>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select payment to refund
              </Typography>
              <RadioGroup
                value={selectedId ?? ''}
                onChange={(e) => {
                  setSelectedId(Number(e.target.value));
                  setAmountDollars('');
                }}
              >
                {payments.map((p) => {
                  const disabled = p.refundable_cents === 0 || !p.is_within_stripe_window;
                  const label = (
                    <Box>
                      <Typography component="span">
                        {new Date(p.created_at).toLocaleDateString()} —{' '}
                        {p.description} —{' '}
                        {formatCents(p.amount_cents, p.currency)}
                      </Typography>
                      <Typography variant="caption" component="div" color="text.secondary">
                        Refundable: {formatCents(p.refundable_cents, p.currency)}
                        {p.refunded_cents > 0 &&
                          ` (already refunded ${formatCents(p.refunded_cents, p.currency)})`}
                        {!p.is_within_stripe_window && ' — past Stripe 180-day window'}
                      </Typography>
                    </Box>
                  );
                  return (
                    <FormControlLabel
                      key={p.payment_id}
                      value={p.payment_id}
                      disabled={disabled}
                      control={<Radio />}
                      label={label}
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>

            {selectedPayment && (
              <>
                {selectedPayment.near_window_warning && (
                  <Alert severity="warning">
                    This charge is more than 90 days old. Verify with Stripe before proceeding.
                  </Alert>
                )}
                {!isStripe && (
                  <Alert severity="info">
                    This payment was not made via Stripe. The refund will be recorded in Cleo and
                    QuickBooks — you must issue payment to the customer manually. The customer will
                    not receive an automatic notification.
                  </Alert>
                )}

                <TextField
                  label={`Refund amount (max ${formatCents(selectedPayment.refundable_cents, selectedPayment.currency)})`}
                  type="number"
                  value={amountDollars}
                  onChange={(e) => setAmountDollars(e.target.value)}
                  inputProps={{
                    min: 0.01,
                    step: 0.01,
                    max: selectedPayment.refundable_cents / 100,
                  }}
                  error={!!amountDollars && !amountValid}
                  helperText={
                    amountDollars && !amountValid
                      ? 'Amount must be greater than zero and within the refundable balance'
                      : ' '
                  }
                  fullWidth
                />

                <FormControl fullWidth>
                  <InputLabel id="refund-reason-label">Reason</InputLabel>
                  <Select
                    labelId="refund-reason-label"
                    label="Reason"
                    value={reason}
                    onChange={(e) => setReason(String(e.target.value))}
                  >
                    <MenuItem value="requested_by_customer">Requested by customer</MenuItem>
                    <MenuItem value="duplicate">Duplicate</MenuItem>
                    <MenuItem value="fraudulent">Fraudulent</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>

                {!isStripe && (
                  <ManualMethodPicker
                    value={qbPaymentMethodId}
                    onChange={(id, label) => {
                      setQbPaymentMethodId(id);
                      setQbPaymentMethodLabel(label);
                    }}
                  />
                )}

                <TextField
                  label="Notes (optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                />
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          variant="contained"
          color="warning"
        >
          {submitting ? 'Processing…' : isStripe ? 'Issue refund via Stripe' : 'Record refund'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ManualMethodPicker: React.FC<{
  value: string;
  onChange: (qbId: string, label: string) => void;
}> = ({ value, onChange }) => {
  const { data, isLoading } = useGetList('paymentmethods', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'Name', order: 'ASC' },
  });

  return (
    <FormControl fullWidth disabled={isLoading}>
      <InputLabel id="refund-method-label">Refund method (must match how payment is sent)</InputLabel>
      <Select
        labelId="refund-method-label"
        label="Refund method (must match how payment is sent)"
        value={value}
        onChange={(e) => {
          const id = String(e.target.value);
          const match = (data || []).find((m: any) => String(m.id) === id);
          onChange(id, match?.Name || '');
        }}
      >
        {(data || []).map((m: any) => (
          <MenuItem key={m.id} value={String(m.id)}>
            {m.Name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default RefundButton;
